import ShipToSelector, { ShipToSelectorStyles } from "@dana/Overrides/Components/ShipToSelector";
import { Cart, danaValidateAddress } from "@dana/Overrides/Services/CartService";
import CustomerAddressForm from "@dana/Overrides/Components/CustomerAddressForm";
import ApplicationStateDana from "@dana/Store/DanaApplicationState";
import { SoldToAddress } from "@dana/Types/ApiModels";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getAddressFieldsDataView } from "@insite/client-framework/Store/Data/AddressFields/AddressFieldsSelector";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import { getShipTosDataView, getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import updateShipTo from "@insite/client-framework/Store/Pages/OnePageCheckout/Handlers/UpdateShipTo";
import translate from "@insite/client-framework/Translate";
import { BaseAddressModel, ShipToModel } from "@insite/client-framework/Types/ApiModels";
import { CustomerAddressFormStyles } from "@insite/content-library/Components/CustomerAddressForm";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { getCookie } from "@insite/mobius/utilities/cookies";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import { getDanaGeneralSettings } from "@dana/Store/Context/ContextSelectors";

interface OwnProps {
    isOpen: boolean;
    onModalClose: () => void;
    extendedStyles?: ShipToSelectorModalStyles;
}

type Props = OwnProps &
    HasHistory &
    HasToasterContext &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

const mapStateToProps = (state: ApplicationStateDana) => {
    const cartMode = getCookie("cartMode");
    const cart =
        cartMode === "EmergencyOrderCart"
            ? ((state.emergencyCartState.cart as Cart) ?? ({ properties: {} } as Cart))
            : cartMode === "StockOrderCart"
                ? getCurrentCartState(state).value
                : ((state.annualReturnsCartState.cart as Cart) ?? ({ properties: {} } as Cart));


    const danaGeneralSettings = getDanaGeneralSettings(state);

    return {
        cart,
        currentShipTo: getShipToState(state, cart ? cart.shipToId : undefined).value,
        shipTosDataView: cart
            ? getShipTosDataView(state, { billToId: cart.billToId, expand: ["validation"], exclude: ["showAll"] })
            : undefined,
        cartPageUrl: getPageLinkByPageType(state, "CartPage")?.url,
        countries: getCurrentCountries(state),
        shipToAddressFields: getAddressFieldsDataView(state).value?.shipToAddressFields,
        danaGeneralSettings
    };
};

const mapDispatchToProps = {
    updateShipTo,
};

export interface ShipToSelectorModalStyles {
    modal?: ModalPresentationProps;
    shipToSelector?: ShipToSelectorStyles;
    buttonsWrapper?: InjectableCss;
    cancelButton?: ButtonPresentationProps;
    addCustomerAddressForm?: CustomerAddressFormStyles;
    editCustomerAddressForm?: CustomerAddressFormStyles;
    errorMessage?: TypographyPresentationProps;
}

export const shipToSelectorModalStyles: ShipToSelectorModalStyles = {
    modal: { sizeVariant: "medium" },
    buttonsWrapper: {
        css: css`
            display: flex;
            justify-content: flex-end;
            margin-top: 1rem;
        `,
    },
    cancelButton: { variant: "secondary" },
};

const ShipToSelectorModal = ({
    cart,
    currentShipTo,
    shipTosDataView,
    cartPageUrl,
    countries,
    shipToAddressFields,
    isOpen,
    extendedStyles,
    history,
    toaster,
    updateShipTo,
    onModalClose,
    danaGeneralSettings
}: Props) => {
    const [styles] = useState(() => mergeToNew(shipToSelectorModalStyles, extendedStyles));

    const [shiToBeingEdited, setShipToBeingEdited] = useState<ShipToModel | undefined>(undefined);
    const [isShipToCreating, setIsShipToCreating] = useState(false);

    const soldToObj: SoldToAddress | undefined = cart?.properties?.soldToAddress
        ? JSON.parse(cart.properties.soldToAddress)
        : undefined;

    const [newAddress, setNewAddress] = useState<ShipToModel | undefined>(undefined);
    const [canCreateNewAddress, setCanCreateNewAddress] = useState<boolean>(true);
    const [validAddress, setValidAddress] = useState<boolean>(true);

    useEffect(() => {
        if (shipTosDataView?.value) {
            const address = shipTosDataView?.value!.find(s => s.isNew && !s.oneTimeAddress);
            if (address) {
                setNewAddress(address);
            } else {
                setCanCreateNewAddress(false);
            }
        }
    }, [shipTosDataView?.value]);

    const selectShipToHandler = (shipTo: ShipToModel) => {
        onModalClose();
        updateShipTo({
            billToId: cart?.billToId || "",
            shipTo,
            isEdit: false,
            onSuccess: hasCartlines => {
                if (!hasCartlines && cartPageUrl) {
                    history.push(cartPageUrl);
                }
            },
        });
    };

    const editShipToHandler = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>, customer: BaseAddressModel) => {
        event.preventDefault();
        setShipToBeingEdited(customer as ShipToModel);
    };

    const createNewAddressHandler = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event.preventDefault();
        setIsShipToCreating(true);
    };

    const isIntegerString = (str: string): boolean => {
        return /^-?\d+$/.test(str.trim()) && Number.isInteger(Number(str));
    };

    const newShipToFormSubmitHandler = (_: React.FormEvent<HTMLFormElement>, address: ShipToModel) => {
        address.oneTimeAddress = true;
        if (danaGeneralSettings?.validateAddress) {
            danaValidateAddress(address).then(validateResult => {
                if (validateResult.results.length > 0 && validateResult.results[0].matchConfidence.score >= 0.89) {
                    const addressResult = validateResult.results[0];
                    const originalPostalCode = address.postalCode;
                    let originalStreetNumber = address.address1.split(" ").at(0);
                    if (!isIntegerString(originalStreetNumber ?? '')) {
                        originalStreetNumber = address.address1.split(" ").reverse().at(0);
                    }
                    address.address1 = `${addressResult.address.streetNumber ?? originalStreetNumber} ${addressResult.address.streetName}`;
                    address.city = addressResult.address.localName;
                    address.postalCode = addressResult.address.extendedPostalCode ?? addressResult.address.postalCode;
                    if (address.postalCode.includes(",")) {
                        address.postalCode =
                            address.postalCode
                                .split(",")
                                .find(postal => postal.trim() === originalPostalCode)
                                ?.trim() ?? address.postalCode;
                        if (address.postalCode.length > 10) {
                            address.postalCode = originalPostalCode;
                        }
                    }
                    const country = countries?.find(c => c.abbreviation === addressResult.address.countryCode);
                    if (country) {
                        address.country = country;
                        const stateOrProvince = country.states?.find(
                            s => s.abbreviation === addressResult.address.countrySubdivisionCode,
                        );
                        if (stateOrProvince) {
                            address.state = stateOrProvince;
                        }
                    }

                    updateShipTo({
                        billToId: cart?.billToId || "",
                        shipTo: address,
                        isEdit: true,
                        onSuccess: hasCartlines => {
                            toaster.addToast({
                                messageType: "success",
                                body: translate("Address has been created."),
                            });
                            if (!hasCartlines && cartPageUrl) {
                                history.push(cartPageUrl);
                            }
                        },
                    });

                    setIsShipToCreating(false);
                    onModalClose();
                } else {
                    setValidAddress(false);
                    setIsShipToCreating(false);
                }
            });
        } else {

            updateShipTo({
                billToId: cart?.billToId || "",
                shipTo: address,
                isEdit: true,
                onSuccess: hasCartlines => {
                    toaster.addToast({
                        messageType: "success",
                        body: translate("Address has been created."),
                    });
                    if (!hasCartlines && cartPageUrl) {
                        history.push(cartPageUrl);
                    }
                },
            });

            setIsShipToCreating(false);
            onModalClose();
        }

    };

    const createNewAddressCancelHandler = () => {
        setIsShipToCreating(false);
    };

    const editShipToFormSubmitHandler = (_: React.FormEvent<HTMLFormElement>, shipTo: ShipToModel) => {
        updateShipTo({
            billToId: cart?.billToId || "",
            shipTo,
            isEdit: true,
            onSuccess: hasCartlines => {
                toaster.addToast({
                    messageType: "success",
                    body: translate("Address updated."),
                });
                if (!hasCartlines && cartPageUrl) {
                    history.push(cartPageUrl);
                }
            },
        });
        setShipToBeingEdited(undefined);
        onModalClose();
    };

    const editAddressCancelHandler = () => {
        setShipToBeingEdited(undefined);
    };

    return (
        <Modal
            {...styles.modal}
            headline={
                isShipToCreating
                    ? translate("Create New Address")
                    : shiToBeingEdited
                        ? translate("Edit Address")
                        : translate("Address Book")
            }
            isOpen={isOpen}
            handleClose={onModalClose}
            data-test-selector="onePageCheckoutShipping_addressModal"
        >
            {!shiToBeingEdited && !isShipToCreating && (
                <>
                    <ShipToSelector
                        currentShipTo={currentShipTo}
                        currentBillToId={cart?.billToId}
                        newAddress={newAddress}
                        onSelect={selectShipToHandler}
                        onEdit={editShipToHandler}
                        onCreateNewAddressClick={createNewAddressHandler}
                        extendedStyles={styles.shipToSelector}
                    />
                    <StyledWrapper {...styles.buttonsWrapper}>
                        <Button {...styles.cancelButton} onClick={onModalClose}>
                            {translate("Cancel")}
                        </Button>
                    </StyledWrapper>
                </>
            )}
            {shiToBeingEdited && countries && shipToAddressFields ? (
                <>
                    {!validAddress ? (
                        <Typography {...styles.errorMessage}>
                            {translate(
                                "Invalid address. Please make sure the address is correct and includes a street number.",
                            )}
                        </Typography>
                    ) : (
                        <></>
                    )}
                    <CustomerAddressForm
                        address={shiToBeingEdited}
                        countries={countries}
                        addressFieldDisplayCollection={shipToAddressFields}
                        onSubmit={editShipToFormSubmitHandler}
                        saveButtonTextOverride={translate("Save & Apply")}
                        onCancel={editAddressCancelHandler}
                        extendedStyles={styles.editCustomerAddressForm}
                    />
                </>
            ) : shiToBeingEdited ? (
                <LoadingSpinner></LoadingSpinner>
            ) : (
                ""
            )}
            {isShipToCreating && newAddress && countries && shipToAddressFields ? (
                <>
                    {!validAddress ? (
                        <Typography {...styles.errorMessage}>
                            {translate(
                                "Invalid address. Please make sure the address is correct and includes a street number.",
                            )}
                            :
                        </Typography>
                    ) : (
                        <></>
                    )}
                    <CustomerAddressForm
                        address={newAddress}
                        countries={countries}
                        addressFieldDisplayCollection={shipToAddressFields}
                        onSubmit={newShipToFormSubmitHandler}
                        onCancel={createNewAddressCancelHandler}
                        extendedStyles={styles.addCustomerAddressForm}
                    />
                </>
            ) : isShipToCreating && canCreateNewAddress ? (
                <LoadingSpinner />
            ) : (
                <></>
            )}
        </Modal>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(withToaster(ShipToSelectorModal)));
